/**
 * Canonical environment variable names for engine URLs.
 *
 * Only these names are supported going forward. Older names are accepted as
 * aliases so existing Vercel / .env.local configs keep working after renames.
 *
 * | Service              | Canonical (set this)           | Legacy aliases                    |
 * |----------------------|--------------------------------|-----------------------------------|
 * | Integrations Engine  | VITE_INTEGRATIONS_API_URL      | VITE_INTEGRATIONS_ENGINE_URL      |
 * | Peak LLM             | VITE_LLM_API_URL               | VITE_PEAK_LLM_URL, VITE_LLM_URL   |
 */

const INTEGRATIONS_ALIASES = [
  'VITE_INTEGRATIONS_API_URL',
  'VITE_INTEGRATIONS_ENGINE_URL',
  'VITE_INTEGRATION_API_URL',
] as const;

const LLM_ALIASES = [
  'VITE_LLM_API_URL',
  'VITE_PEAK_LLM_URL',
  'VITE_LLM_URL',
] as const;

/** Deployed integrations-engine (Vercel project: integrations-engine). */
export const INTEGRATIONS_ENGINE_PRODUCTION_URL = 'https://integrations-engine.vercel.app';

function readEnv(keys: readonly string[]): string | undefined {
  const env = import.meta.env as Record<string, string | undefined>;
  for (const key of keys) {
    const value = env[key]?.trim();
    if (value) return value.replace(/\/$/, '');
  }
  return undefined;
}

/** Integrations Engine base URL (Miro, Jira, Slack, …). */
export function resolveIntegrationsEngineUrl(): string {
  return (
    readEnv(INTEGRATIONS_ALIASES) ??
    (import.meta.env.PROD ? INTEGRATIONS_ENGINE_PRODUCTION_URL : 'http://localhost:4100')
  );
}

/**
 * Peak LLM base URL. When unset, callers use same-origin `/api/llm` (Vite proxy
 * locally; Vercel serverless `api/llm/*` in production).
 */
export function resolveLlmApiBaseUrl(): string | undefined {
  return readEnv(LLM_ALIASES);
}

/** POST target for stateless chat (peak-llm `/api/chat/simple` contract). */
export function resolveLlmChatSimpleUrl(): string {
  const base = resolveLlmApiBaseUrl();
  return base ? `${base}/api/chat/simple` : '/api/llm/chat/simple';
}

/** Names the UI / status page should tell operators to set. */
export const CANONICAL_ENV = {
  integrations: 'VITE_INTEGRATIONS_API_URL',
  llm: 'VITE_LLM_API_URL',
} as const;
